import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { navLinks, contactLinks } from "../data/mock";
import { Download, Sun, Moon, Menu, X } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

// ---------------------------------------------------------------------------
// Resume download helper — Google Docs export URL is cross-origin, so we
// try a fetch + blob trigger first, and fall back to opening in a new tab.
// ---------------------------------------------------------------------------
const downloadResume = async () => {
  try {
    const res = await fetch(contactLinks.resume);
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Shweta_Chauhan_Resume.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch {
    window.open(contactLinks.resume, "_blank", "noopener");
  }
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const NavbarLogo = () => (
  <Link to="/" className="flex items-center gap-2 group">
    <span className="w-7 h-7 rounded-md bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 flex items-center justify-center text-[18px] font-brand font-semibold leading-none shrink-0 pb-0.5">
      S
    </span>
    <span className="text-[15px] font-medium tracking-tight text-neutral-900 dark:text-neutral-50">
      Shweta Chauhan
    </span>
    <span className="hidden sm:inline text-[13px] text-neutral-500 dark:text-neutral-400 ml-1">
      — Product Designer
    </span>
  </Link>
);

const DesktopLinks = () => (
  <nav className="hidden md:flex items-center gap-8">
    {navLinks.map((link) => (
      <a
        key={link.href}
        href={link.href}
        className="text-[14px] text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors duration-200"
      >
        {link.label}
      </a>
    ))}
  </nav>
);

const ThemeToggleButton = ({ theme, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label="Toggle theme"
    className="w-9 h-9 rounded-full border border-neutral-200 dark:border-neutral-800 bg-white/60 dark:bg-neutral-900/60 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-50 hover:border-neutral-300 dark:hover:border-neutral-700 flex items-center justify-center transition-colors duration-200"
  >
    {theme === "dark" ? (
      <Sun className="w-4 h-4" strokeWidth={2} />
    ) : (
      <Moon className="w-4 h-4" strokeWidth={2} />
    )}
  </button>
);

const ResumeButton = () => (
  <button
    type="button"
    onClick={downloadResume}
    data-testid="navbar-resume-button"
    className="hidden sm:inline-flex items-center gap-1.5 text-[14px] font-medium text-white dark:text-neutral-900 bg-neutral-900 dark:bg-white hover:bg-neutral-700 dark:hover:bg-neutral-200 px-4 py-2 rounded-full transition-colors duration-200"
  >
    Resume
    <Download className="w-3.5 h-3.5" strokeWidth={2.25} />
  </button>
);

const MobileToggleButton = ({ open, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={open ? "Close menu" : "Open menu"}
    aria-expanded={open}
    data-testid="navbar-mobile-toggle"
    className="md:hidden w-9 h-9 rounded-full border border-neutral-200 dark:border-neutral-800 bg-white/60 dark:bg-neutral-900/60 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-50 hover:border-neutral-300 dark:hover:border-neutral-700 flex items-center justify-center transition-colors duration-200 relative"
  >
    <span
      className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
        open ? "opacity-0 rotate-90 scale-75" : "opacity-100 rotate-0 scale-100"
      }`}
    >
      <Menu className="w-4 h-4" strokeWidth={2.25} />
    </span>
    <span
      className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
        open ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-75"
      }`}
    >
      <X className="w-4 h-4" strokeWidth={2.25} />
    </span>
  </button>
);

const MobileSheet = ({ open, onClose }) => (
  <div
    className={`md:hidden grid transition-[grid-template-rows,opacity] duration-[420ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
      open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0 pointer-events-none"
    }`}
    data-testid="navbar-mobile-menu"
    aria-hidden={!open}
  >
    <div className="overflow-hidden min-h-0">
      <nav className="px-6 pb-6 pt-2 flex flex-col gap-1 border-t border-neutral-200/60 dark:border-neutral-800/60">
        {navLinks.map((link, i) => (
          <a
            key={link.href}
            href={link.href}
            onClick={onClose}
            style={{ transitionDelay: open ? `${80 + i * 50}ms` : "0ms" }}
            className={`px-3 py-3 rounded-lg text-[15px] text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-900 hover:text-neutral-900 dark:hover:text-neutral-50 transform transition-[opacity,transform,background-color,color] duration-300 ease-out ${
              open ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1.5"
            }`}
          >
            {link.label}
          </a>
        ))}
        <button
          type="button"
          onClick={() => {
            onClose();
            downloadResume();
          }}
          data-testid="navbar-mobile-resume-button"
          style={{ transitionDelay: open ? `${80 + navLinks.length * 50}ms` : "0ms" }}
          className={`mt-2 inline-flex items-center justify-between gap-1.5 text-[14.5px] font-medium text-white dark:text-neutral-900 bg-neutral-900 dark:bg-white hover:bg-neutral-700 dark:hover:bg-neutral-200 px-4 py-3 rounded-full transform transition-[opacity,transform,background-color] duration-300 ease-out ${
            open ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1.5"
          }`}
        >
          <span>Resume</span>
          <Download className="w-4 h-4" strokeWidth={2.25} />
        </button>
      </nav>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Custom hooks for navbar state
// ---------------------------------------------------------------------------
const useScrolled = (threshold = 12) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
};

const useBodyScrollLock = (locked) => {
  useEffect(() => {
    document.body.style.overflow = locked ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [locked]);
};

// ---------------------------------------------------------------------------
// Navbar
// ---------------------------------------------------------------------------
const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const scrolled = useScrolled();

  useBodyScrollLock(mobileOpen);

  // Close the mobile menu whenever we navigate
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const toggleMobile = useCallback(() => setMobileOpen((v) => !v), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const opaque = scrolled || !isHome || mobileOpen;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[backdrop-filter,background-color,border-color] duration-300 ${
        opaque
          ? "bg-white/85 dark:bg-neutral-950/85 backdrop-blur-md border-b border-neutral-200/70 dark:border-neutral-800/70"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <NavbarLogo />
        <DesktopLinks />
        <div className="flex items-center gap-2">
          <ThemeToggleButton theme={theme} onToggle={toggleTheme} />
          <ResumeButton />
          <MobileToggleButton open={mobileOpen} onToggle={toggleMobile} />
        </div>
      </div>

      <MobileSheet open={mobileOpen} onClose={closeMobile} />
    </header>
  );
};

export default Navbar;
